import { Button, ClickAwayListener, IconFont, Input, Menu, Modal, Text, Toast } from '@byecode/ui'
import { TooltipText, useAtomAction } from '@lighthouse/shared'
import { Panel } from '@xyflow/react'
import { findIndex } from 'rambda'
import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import styled from 'styled-components'

import { workflowAtomFamily, workflowListAtom } from '@/atoms/workflow/state'
import { FlowDetailShape, useFlowContainerContext } from '@/contexts/FlowContext'
import * as srv from '@/services'

const SCxPanel = styled(Panel)`
    max-width: 50%;
    display: flex;
    gap: 4px;
    height: 36px;
    align-items: center;
`

const SCxPageTitlePreviewer = styled(Text)`
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    &:hover {
        background-color: var(--color-gray-100);
    }
`

const SCxPageTitleInput = styled(Input)`
    width: 400px;
    resize: none;
    overflow: hidden;
`

const ReadonlyTag = styled.div`
    align-self: center;
    padding: 0 4px;
    color: var(--color-main);
    font-size: 12px;
    line-height: 20px;
    border-radius: 4px;
    white-space: nowrap;
    border: 1px solid var(--color-purple-300);
    background-color: var(--color-white);
`

export const LeftActionPanel: React.FC = () => {
    const { id, name, log, shape, onClose } = useFlowContainerContext()
    const readonly = !!log
    const [titleMode, setTitleMode] = useState<boolean>(true)
    const closeAfterDelete = useMemo(() => shape === FlowDetailShape.MODAL || shape === FlowDetailShape.LOG_MODAL, [shape])
    const { run: setWorkflowListAtom } = useAtomAction(workflowListAtom)
    const { run: updateWorkflow } = useAtomAction(workflowAtomFamily(id))
    const navigate = useNavigate()

    const handleBack = useCallback(() => {
        navigate({ pathname: '../flow' })
    }, [navigate])

    const handleChangeTitleMode = useCallback(() => {
        if (readonly) {
            return
        }
        setTitleMode(false)
    }, [readonly])

    const handleNameChange = useCallback(
        async (val: string) => {
            const oldName = name
            updateWorkflow(draft => {
                if (!draft) {
                    return
                }
                draft.name = val
            })
            const isUpdate = await srv.updateFlow({ id, name: val })
            if (!isUpdate && oldName) {
                updateWorkflow(draft => {
                    if (!draft) {
                        return
                    }
                    draft.name = oldName
                })
            }
        },
        [id, name, updateWorkflow]
    )

    const handleOpenRename = useCallback(() => {
        setTitleMode(false)
    }, [])

    const handleDelete = useCallback(async () => {
        const isDeleted = await Modal.confirm({
            title: '删除工作流',
            content: `确认删除“${name ?? '未命名'}”工作流？删除后不可恢复，请谨慎操作`
        })
        if (!isDeleted) {
            return
        }
        const isDelete = await srv.deleteFlow(id)
        if (!isDelete) {
            return
        }
        setWorkflowListAtom(draft => {
            const index = findIndex(item => item.id === id, draft)
            draft.splice(index, 1)
        })
        Toast.success('删除成功')
        if (closeAfterDelete) {
            onClose?.()
            return
        }
        handleBack()
    }, [closeAfterDelete, handleBack, id, name, onClose, setWorkflowListAtom])

    return (
        <SCxPanel position="top-left">
            {shape === FlowDetailShape.PAGE && (
                <Button
                    style={{ width: 24, height: 24, padding: 4 }}
                    size="sm"
                    type="text"
                    icon={<IconFont size={16} fill="var(--color-gray-400)" type="ArrowLeftSmall" />}
                    onClick={handleBack}
                />
            )}
            {titleMode ? (
                <TooltipText
                    title={name}
                    render={ref => (
                        <SCxPageTitlePreviewer ref={ref} size={16} color="var(--color-black)" onClick={handleChangeTitleMode}>
                            {name}
                        </SCxPageTitlePreviewer>
                    )}
                />
            ) : (
                <ClickAwayListener
                    onClickAway={() => {
                        setTitleMode(true)
                    }}
                >
                    <SCxPageTitleInput
                        autoSelect
                        defaultValue={name}
                        onBlur={ev => {
                            handleNameChange?.(ev.target.value)
                            setTitleMode(true)
                        }}
                        onKeyDownCapture={ev => {
                            if (ev.key === 'Enter') {
                                handleNameChange?.(ev.currentTarget.value)
                            }
                        }}
                    />
                </ClickAwayListener>
            )}
            <Menu width={220} closeOnItemClick returnFocus={false}>
                <Menu.Target>
                    <Button
                        style={{ width: 24, height: 24, padding: 4 }}
                        size="sm"
                        type="text"
                        icon={<IconFont size={16} fill="var(--color-gray-400)" type="DotsThree" />}
                    />
                </Menu.Target>
                <Menu.Dropdown>
                    <Menu.Item onClick={handleOpenRename} icon={<IconFont type="NotePencil" />}>
                        重命名
                    </Menu.Item>
                    <Menu.Item icon={<IconFont type="Trash" />} onClick={handleDelete}>
                        删除
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>

            {readonly && <ReadonlyTag>只读</ReadonlyTag>}
        </SCxPanel>
    )
}
